import { useEffect, useMemo, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { LocalAiLeadGroupOverviewResponse, LocalAiLeadGroupOverviewRow } from "@/api/admin-api"

import { updateLocalAiLeadHeadcountOverride } from "@/api/admin-api"
import { Building2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import { DashboardSectionCard } from "./admin-dashboard-shared"

type Row = LocalAiLeadGroupOverviewRow
type SortKey =
  | "groupValue"
  | "employees"
  | "headcountUpdatedAt"
  | "activeUsers"
  | "adoption"
  | "requestsPerUser3m"
  | "requestsPerUserLastMonth"
  | "tokenSharePct"
  | "usageMonthly"
  | "sharedMonthly"
  | "totalMonthly"
  | "perUserMonthly"

function compareRows(a: Row, b: Row, key: SortKey): number {
  const aValue = a[key]
  const bValue = b[key]
  if (aValue == null && bValue == null) return 0
  if (aValue == null) return 1
  if (bValue == null) return -1
  if (typeof aValue === "string" && typeof bValue === "string") return aValue.localeCompare(bValue)
  return Number(aValue) - Number(bValue)
}

function trendSymbol(trend: Row["requestTrend"]): string {
  if (trend === "up") return "▲"
  if (trend === "down") return "▼"
  if (trend === "flat") return "•"
  return ""
}

export function LocalAiLeadSteeringOverview({
  data,
  canEditOverride,
}: {
  data: LocalAiLeadGroupOverviewResponse
  canEditOverride: boolean
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const summary = data.summary!
  const currency = data.currency
  const [sortKey, setSortKey] = useState<SortKey>("activeUsers")
  const [descending, setDescending] = useState(true)
  const [overrideDraft, setOverrideDraft] = useState("")
  const [overrideDirty, setOverrideDirty] = useState(false)
  useEffect(() => {
    if (!overrideDirty) {
      setOverrideDraft(
        summary.groupEmployeesSource === "manual" ? String(summary.groupEmployees) : ""
      )
    }
  }, [overrideDirty, summary.groupEmployees, summary.groupEmployeesSource])
  const mutation = useMutation({
    mutationFn: updateLocalAiLeadHeadcountOverride,
    onSuccess: async () => {
      setOverrideDirty(false)
      await queryClient.invalidateQueries({ queryKey: ["admin", "local-ai-lead-group-overview"] })
    },
  })

  const money = (amount: number | null) =>
    amount == null
      ? "—"
      : new Intl.NumberFormat("sv-SE", {
          style: "currency",
          currency,
          maximumFractionDigits: 2,
        }).format(amount)
  const number = (amount: number | null | undefined) =>
    amount == null ? "—" : amount.toLocaleString("sv-SE")
  const percent = (amount: number | null | undefined) =>
    amount == null ? "—" : `${amount.toFixed(1)}%`
  const sorted = useMemo(
    () =>
      [...data.groups].sort((a, b) => {
        const aMissing = a[sortKey] == null
        const bMissing = b[sortKey] == null
        if (aMissing !== bMissing) return aMissing ? 1 : -1
        const compared = compareRows(a, b, sortKey)
        if (compared) return descending ? -compared : compared
        return (a.groupValue || "").localeCompare(b.groupValue || "")
      }),
    [data.groups, descending, sortKey]
  )
  const chooseSort = (key: SortKey) => {
    if (key === sortKey) setDescending((value) => !value)
    else {
      setSortKey(key)
      setDescending(true)
    }
  }
  const header = (key: SortKey, label: string) => {
    let indicator = "↕"
    if (sortKey === key) indicator = descending ? "↓" : "↑"
    return (
      <button
        aria-label={t("localAiLead.steering.sortBy", "Sort by {{label}}", { label })}
        type="button"
        onClick={() => chooseSort(key)}
      >
        {label} {indicator}
      </button>
    )
  }
  const saveOverride = async (employees: number | null) => {
    try {
      await mutation.mutateAsync({ employees, expectedUpdatedAt: summary.groupHeadcountUpdatedAt })
      toast.success(t("localAiLead.steering.headcountSaved", "Group headcount updated"))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }
  const parsedOverride = Number(overrideDraft)
  const overrideValid = Number.isInteger(parsedOverride) && parsedOverride > 0
  const sharedPct =
    summary.totalMonthly > 0 ? (summary.sharedPoolMonthly / summary.totalMonthly) * 100 : 0
  const trendLabels = {
    up: t("localAiLead.steering.trendUp", "rising"),
    down: t("localAiLead.steering.trendDown", "falling"),
    flat: t("localAiLead.steering.trendFlat", "unchanged"),
  }

  return (
    <DashboardSectionCard
      icon={Building2}
      id="local-ai-lead-group-overview"
      title={t("localAiLead.steering.title", "Group-wide cost and adoption")}
      description={t(
        "localAiLead.steering.description",
        "Monthly estimate for {{month}} with company-level adoption and usage.",
        { month: data.costMonth }
      )}
    >
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="bg-muted/40 rounded-lg border p-4">
          <p className="text-muted-foreground text-xs">
            {t("localAiLead.steering.annualTotal", "Annual cost estimate")}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {money(summary.annualTotalEstimate)}
          </p>
        </div>
        <div className="bg-muted/40 rounded-lg border p-4">
          <p className="text-muted-foreground text-xs">
            {t("localAiLead.steering.annualPerUser", "Annual cost per active user")}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {money(summary.annualPerActiveUser)}
          </p>
        </div>
        <div className="bg-muted/40 rounded-lg border p-4">
          <p className="text-muted-foreground text-xs">
            {t("localAiLead.steering.groupAdoption", "Group adoption")}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {percent(summary.groupAdoption)}
          </p>
          <p className="text-muted-foreground text-xs">
            {summary.outstandingCompanies}{" "}
            {t("localAiLead.steering.outstanding", "companies missing headcount")}
          </p>
        </div>
        <div className="bg-muted/40 rounded-lg border p-4">
          <p className="text-muted-foreground text-xs">
            {t("localAiLead.steering.benchmarkDifference", "Difference to seat benchmark · annual")}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {money(summary.benchmarkDifference)}
          </p>
          <p className="text-muted-foreground text-xs">
            {t(
              "localAiLead.steering.benchmarkNote",
              "Estimate compared with the same active users on the reference seat price."
            )}
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-lg border p-4">
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          <span>
            {t("localAiLead.steering.usage", "Usage")}: {money(summary.usageMonthly)}
          </span>
          <span>
            {t("localAiLead.steering.shared", "Shared services")}:{" "}
            {money(summary.sharedPoolMonthly)}
          </span>
          <span className="font-medium">
            {t("localAiLead.steering.totalMonth", "Total / month")}: {money(summary.totalMonthly)}
          </span>
        </div>
        <div
          className="bg-muted mt-3 flex h-3 overflow-hidden rounded-full"
          role="img"
          aria-label={t(
            "localAiLead.steering.costSplit",
            "Cost split: {{shared}}% shared services",
            { shared: sharedPct.toFixed(1) }
          )}
        >
          <span className="bg-primary h-full" style={{ width: `${Math.min(sharedPct, 100)}%` }} />
          <span className="bg-secondary h-full flex-1" />
        </div>
      </div>

      <div className="mt-4 rounded-lg border p-4">
        <p className="text-sm font-medium">
          {t("localAiLead.steering.employees", "Group employees")}: {number(summary.groupEmployees)}
        </p>
        <p className="text-muted-foreground text-xs">
          {t(
            "localAiLead.steering.reported",
            "{{sum}} reported by {{companies}} companies; {{missing}} outstanding.",
            {
              sum: number(summary.reportedEmployees),
              companies: summary.reportingCompanies,
              missing: summary.outstandingCompanies,
            }
          )}{" "}
          {summary.groupEmployeesSource === "manual"
            ? t("localAiLead.steering.manual", "Manual group total in use.")
            : ""}
        </p>
        {canEditOverride ? (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs" htmlFor="lail-group-headcount-override">
                {t("localAiLead.steering.override", "Manual group total")}
              </label>
              <Input
                className="w-40"
                id="lail-group-headcount-override"
                min={1}
                step={1}
                type="number"
                value={overrideDraft}
                onChange={(event) => {
                  setOverrideDraft(event.target.value)
                  setOverrideDirty(true)
                }}
              />
            </div>
            <Button
              aria-label={t("localAiLead.steering.saveOverride", "Save group headcount override")}
              disabled={!overrideValid || mutation.isPending}
              size="sm"
              onClick={() => void saveOverride(parsedOverride)}
            >
              {t("common.save", "Save")}
            </Button>
            {summary.groupEmployeesSource === "manual" ? (
              <Button
                disabled={mutation.isPending}
                size="sm"
                variant="outline"
                onClick={() => void saveOverride(null)}
              >
                {t("localAiLead.steering.useReported", "Use reported sum")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {summary.historyMonthsAvailable < 3 ? (
        <p className="text-muted-foreground mt-4 text-xs">
          {t(
            "localAiLead.steering.historyCoverage",
            "Request trends need three completed months after enablement; {{count}} available.",
            {
              count: summary.historyMonthsAvailable,
            }
          )}
        </p>
      ) : null}
      <div className="mt-4 overflow-x-auto">
        <Table className="min-w-[1250px]">
          <TableHeader>
            <TableRow>
              <TableHead>
                {header("groupValue", t("localAiLead.steering.company", "Company"))}
              </TableHead>
              <TableHead className="text-right">
                {header("employees", t("localAiLead.steering.headcount", "Employees"))}
              </TableHead>
              <TableHead>
                {header(
                  "headcountUpdatedAt",
                  t("localAiLead.steering.updated", "Headcount updated")
                )}
              </TableHead>
              <TableHead className="text-right">
                {header("activeUsers", t("localAiLead.steering.users", "Users"))}
              </TableHead>
              <TableHead className="text-right">
                {header("adoption", t("localAiLead.steering.adoption", "Adoption"))}
              </TableHead>
              <TableHead className="text-right">
                {header("requestsPerUser3m", t("localAiLead.steering.requests3m", "Req/user · 3m"))}
              </TableHead>
              <TableHead className="text-right">
                {header(
                  "requestsPerUserLastMonth",
                  t("localAiLead.steering.requestsLast", "Req/user · last month")
                )}
              </TableHead>
              <TableHead className="text-right">
                {header("tokenSharePct", t("localAiLead.steering.tokenShare", "Token share"))}
              </TableHead>
              <TableHead className="text-right">
                {header("usageMonthly", t("localAiLead.steering.usage", "Usage"))}
              </TableHead>
              <TableHead className="text-right">
                {header("sharedMonthly", t("localAiLead.steering.shared", "Shared"))}
              </TableHead>
              <TableHead className="text-right">
                {header("totalMonthly", t("localAiLead.steering.totalMonth", "Total / month"))}
              </TableHead>
              <TableHead className="text-right">
                {header("perUserMonthly", t("localAiLead.steering.perUser", "Per user"))}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.groupValue ?? "unassigned"}>
                <TableCell className="font-medium">
                  {row.groupValue ??
                    t("localAiLead.steering.unassigned", "Unassigned / unallocated")}
                </TableCell>
                <TableCell className="text-right tabular-nums">{number(row.employees)}</TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {row.headcountUpdatedAt
                    ? new Date(row.headcountUpdatedAt).toLocaleDateString()
                    : "—"}
                  {row.headcountUpdatedBy ? (
                    <span className="block">
                      {t("localAiLead.steering.updatedBy", "By {{actor}}", {
                        actor: row.headcountUpdatedBy,
                      })}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="text-right tabular-nums">{number(row.activeUsers)}</TableCell>
                <TableCell className="text-right tabular-nums">{percent(row.adoption)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {number(row.requestsPerUser3m)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {number(row.requestsPerUserLastMonth)}{" "}
                  {row.requestTrend ? (
                    <span
                      aria-label={trendLabels[row.requestTrend]}
                      role="img"
                      title={trendLabels[row.requestTrend]}
                    >
                      {trendSymbol(row.requestTrend)}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {percent(row.tokenSharePct)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{money(row.usageMonthly)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(row.sharedMonthly)}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {money(row.totalMonthly)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {money(row.perUserMonthly)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardSectionCard>
  )
}
